// Detailseite einer dokumentierten Totara-Einstellung.
//
// Stammdaten (Kategorie, Bezeichnung, Wert, Geltung, Autor) plus eine
// ausführliche Markdown-Beschreibung – gepflegt mit dem gleichen Toast-UI-
// Editor wie die Mandanten-Notizen. Alles wird per PUT zurückgeschrieben.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api.js';
import MarkdownEditor from './MarkdownEditor.jsx';
import ScopeSelect, { formatScope } from './ScopeSelect.jsx';

const EMPTY_FORM = {
    category: '',
    name: '',
    value: '',
    description: '',
    author: '',
    mandant_id: null,
    course_set: null,
};

export default function TotaraSettingDetail() {
    const { id: idParam } = useParams();
    const navigate = useNavigate();
    const id = Number(idParam);

    const [data, setData] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [mandanten, setMandanten] = useState([]);
    const [categories, setCategories] = useState([]);
    const [courseSets, setCourseSets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saveError, setSaveError] = useState('');
    const [saving, setSaving] = useState(false);
    const [savedNotice, setSavedNotice] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoadError('');
            const d = await api.getTotaraSetting(id);
            setData(d);
            setForm({
                category: d.category ?? '',
                name: d.name ?? '',
                value: d.value ?? '',
                description: d.description ?? '',
                author: d.author ?? '',
                mandant_id: d.mandant_id ?? null,
                course_set: d.course_set ?? null,
            });
        } catch (err) {
            setLoadError(err.message);
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        load();
        api.listMandanten().then(setMandanten).catch(() => { /* unkritisch */ });
        api.listTotaraCategories().then(setCategories).catch(() => { /* unkritisch */ });
        api.listTotaraCourseSets().then(setCourseSets).catch(() => { /* unkritisch */ });
    }, [load]);

    function updateField(field, value) {
        setForm((f) => ({ ...f, [field]: value }));
        setSavedNotice(false);
    }

    async function handleSave(e) {
        e.preventDefault();
        setSaving(true);
        setSaveError('');
        setSavedNotice(false);
        try {
            const updated = await api.updateTotaraSetting(id, {
                category: form.category || 'Allgemein',
                name: form.name,
                value: form.value,
                description: form.description,
                author: form.author,
                mandant_id: form.mandant_id,
                course_set: form.course_set,
            });
            setData(updated);
            setSavedNotice(true);
        } catch (err) {
            setSaveError(err.message);
        } finally {
            setSaving(false);
        }
    }

    async function handleDelete() {
        if (!window.confirm('Diese Einstellung wirklich löschen?')) return;
        try {
            await api.deleteTotaraSetting(id);
            navigate('/totara');
        } catch (err) {
            alert('Löschen fehlgeschlagen: ' + err.message);
        }
    }

    if (loading) return <div className="empty">Lade…</div>;
    if (loadError) {
        return (
            <div>
                <Link to="/totara">← Zurück zur Übersicht</Link>
                <div className="panel" style={{ color: 'var(--color-danger)', marginTop: 12 }}>
                    Fehler: {loadError}
                </div>
            </div>
        );
    }

    return (
        <div className="page-centered page-centered--wide">
            <div style={{ marginBottom: 8 }}>
                <Link to="/totara">← Zurück zur Übersicht</Link>
            </div>

            <div className="toolbar">
                <h1 style={{ margin: 0 }}>{data.name}</h1>
                <span className="muted">
                    {data.category}
                    {' · '}
                    {formatScope(data)}
                </span>
                <div className="spacer" />
                <button className="btn btn--danger" onClick={handleDelete}>
                    Einstellung löschen
                </button>
            </div>

            <form className="panel" onSubmit={handleSave}>
                <h2>Bearbeiten</h2>
                {saveError && <div className="login__error">{saveError}</div>}
                {savedNotice && (
                    <div
                        className="panel"
                        style={{ color: 'var(--color-success)', marginBottom: 12, padding: 8 }}
                    >
                        Gespeichert.
                    </div>
                )}

                <div className="grid-2">
                    <div className="field">
                        <label>Kategorie</label>
                        <input
                            type="text"
                            list="totara-categories-detail"
                            value={form.category}
                            onChange={(e) => updateField('category', e.target.value)}
                        />
                        <datalist id="totara-categories-detail">
                            {categories.map((c) => <option key={c} value={c} />)}
                        </datalist>
                    </div>
                    <div className="field">
                        <label>Geltung</label>
                        <ScopeSelect
                            value={{ mandant_id: form.mandant_id, course_set: form.course_set }}
                            onChange={(next) => {
                                setForm((f) => ({ ...f, ...next }));
                                setSavedNotice(false);
                            }}
                            mandanten={mandanten}
                            courseSets={courseSets}
                        />
                    </div>
                </div>

                <div className="field">
                    <label>Bezeichnung *</label>
                    <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => updateField('name', e.target.value)}
                    />
                </div>

                <div className="grid-2">
                    <div className="field">
                        <label>Wert</label>
                        <input
                            type="text"
                            value={form.value}
                            onChange={(e) => updateField('value', e.target.value)}
                        />
                    </div>
                    <div className="field">
                        <label>Autor</label>
                        <input
                            type="text"
                            value={form.author}
                            onChange={(e) => updateField('author', e.target.value)}
                        />
                    </div>
                </div>

                <div className="field">
                    <label>Beschreibung (Markdown)</label>
                    <MarkdownEditor
                        value={form.description}
                        onChange={(md) => updateField('description', md)}
                        height="420px"
                    />
                </div>

                <div className="muted" style={{ fontSize: 12, margin: '12px 0' }}>
                    Angelegt am {formatDate(data.created_at)}
                    {data.updated_at && ` · zuletzt geändert am ${formatDate(data.updated_at)}`}
                </div>

                <button type="submit" className="btn btn--primary" disabled={saving}>
                    {saving ? 'Speichere…' : 'Speichern'}
                </button>
            </form>
        </div>
    );
}

function formatDate(s) {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return s;
    return d.toLocaleString('de-DE');
}
